/**
 * Hand-rolled UI primitives — buttons, switch, badge, card, inputs, and the small
 * loading/empty/error states. No component library: everything is a thin, accessible
 * wrapper over native elements styled by `app.css`.
 */
import { useState } from 'react';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

import { IconAlert, IconSearch } from './icons.js';
import { useToast } from './Toast.js';

type Variant = 'primary' | 'accent' | 'danger' | 'ghost' | 'default';

export function Button({
  variant = 'default',
  size,
  loading = false,
  block = false,
  children,
  className,
  disabled,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: 'sm';
  loading?: boolean;
  block?: boolean;
}): JSX.Element {
  const classes = [
    'btn',
    variant !== 'default' ? `btn--${variant}` : '',
    size === 'sm' ? 'btn--sm' : '',
    block ? 'btn--block' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button className={classes} disabled={disabled === true || loading} {...rest}>
      {loading && <span className="btn__spinner" aria-hidden />}
      {children}
    </button>
  );
}

export function Switch({
  checked,
  onChange,
  disabled = false,
  size,
  label,
  title,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  size?: 'lg';
  /**
   * The accessible name. Announced, never drawn.
   *
   * Most callers pass an ACTION here — "Toggle gift_cards", "Switch this brand off", "Enable
   * paystack" — which is right for a screen reader and wrong as visible text beside a control.
   * That is why this one is not rendered and `title` exists separately.
   */
  label?: string;
  /**
   * A visible name, for a switch that has none of its own on the page.
   *
   * Opt-in rather than automatic, and the reason is that most switches in this console already
   * sit beside their own text — a `field__label` above, a `<span>` after, a flag row that names
   * the flag. Drawing `label` for all of them would print the name twice.
   *
   * Where it IS needed the absence is bad: the trading page stacks two unlabelled toggles with
   * a paragraph under each, and they are "practice mode" and "halt all trading for every
   * customer". Passing `title` names them and also serves as the accessible name, so a caller
   * cannot supply one without the other.
   */
  title?: string;
}): JSX.Element {
  return (
    <label className={`switch${size === 'lg' ? ' switch--lg' : ''}`}>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        aria-label={title ?? label}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="switch__track">
        <span className="switch__thumb" />
      </span>
      {title !== undefined && <span className="switch__label">{title}</span>}
    </label>
  );
}

export type Tone = 'green' | 'violet' | 'red' | 'amber' | 'blue' | 'neutral';

export function Badge({
  tone = 'neutral',
  dot = false,
  children,
}: {
  tone?: Tone;
  dot?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <span className={`badge badge--${tone}`}>
      {dot && <span className="badge__dot" />}
      {children}
    </span>
  );
}

export function Card({
  title,
  actions,
  children,
  flush = false,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  flush?: boolean;
  className?: string;
}): JSX.Element {
  return (
    <section className={`card${className ? ` ${className}` : ''}`}>
      {(title !== undefined || actions !== undefined) && (
        <header className="card__head">
          <h3 className="card__title">{title}</h3>
          {actions}
        </header>
      )}
      <div className={`card__body${flush ? ' card__body--flush' : ''}`}>{children}</div>
    </section>
  );
}

export function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="field">
      <label className="field__label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
    </div>
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>): JSX.Element {
  return <input className={`input${props.className ? ` ${props.className}` : ''}`} {...props} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>): JSX.Element {
  return <textarea className="input" {...props} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>): JSX.Element {
  return <select className="select" {...props} />;
}

export function SearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}): JSX.Element {
  return (
    <div className="search">
      <span className="search__icon">
        <IconSearch size={16} />
      </span>
      <input
        className="input"
        type="search"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function Spinner(): JSX.Element {
  return <div className="spinner" role="status" aria-label="Loading" />;
}

export function LoadingState({ label = 'Loading…' }: { label?: string }): JSX.Element {
  return (
    <div className="loading">
      <Spinner />
      <div className="muted">{label}</div>
    </div>
  );
}

export function EmptyState({
  glyph = '–',
  title,
  hint,
}: {
  glyph?: string;
  title: string;
  hint?: string;
}): JSX.Element {
  return (
    <div className="empty">
      <div className="empty__glyph">{glyph}</div>
      <div className="empty__title">{title}</div>
      {hint !== undefined && <div className="empty__hint">{hint}</div>}
    </div>
  );
}

export function ErrorBox({ message }: { message: string }): JSX.Element {
  return (
    <div className="error-box" role="alert">
      <IconAlert size={18} />
      <div>{message}</div>
    </div>
  );
}

/**
 * Copy one value verbatim.
 *
 * The console's only copy control, shared rather than reimplemented per page, because the
 * two things it is used for fail the same way and must therefore behave the same way.
 * Support staff move a contact value into a ticket and read it down a phone line; an
 * operator moves a callback URL into a vendor dashboard. In both cases a value that was
 * retyped from a screen is a value that can be wrong, and in the webhook case wrong is
 * silent — the vendor's delivery log shows green against somebody else's 404.
 *
 * It copies exactly the characters it was given and unmasks nothing.
 */
export function CopyButton({ value, label }: { value: string; label: string }): JSX.Element {
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard access is permission-gated and can simply be refused; say so rather
      // than leaving a button that silently does nothing.
      toast.error(
        'Could not copy',
        'Your browser blocked clipboard access. Select the text instead.',
      );
    }
  }

  return (
    <button
      type="button"
      className="copy-btn"
      aria-label={`Copy ${label}`}
      title={`Copy ${label}`}
      onClick={() => void copy()}
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}
